var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.codex",
  title: "EGT Codex page",
  description: "Renders the EGT Codex verbatim from markdown with a skin + section TOC.",
  supportServer: true
});
var fm = require("../../uam/filemanager.js");
var mdr = require("../helpers/egt.markdown.js");
var S = require("../helpers/egt.sections.js");

// ---------------------------------------------------------------------------
// In-document "Table of Contents" -> real anchors (SKIN ONLY).
// The Codex markdown (agents.md §4) renders VERBATIM and must never be edited,
// and it carries its own plain-text TOC list before the PREAMBLE — so the page
// showed two contents lists, one of them dead. We fix that in the RENDERER:
// wrap each entry's existing nodes in an <a> pointing at the SAME heading id
// the sidebar rail uses (mdr's slug()). No text is added, removed, reordered
// or re-emphasised — only wrapped. An entry we cannot confidently match to a
// heading is LEFT AS PLAIN TEXT (a dead link on a legal page is worse).
// ---------------------------------------------------------------------------

// Flatten a UA/JSON node tree back to its plain text, for matching only.
function plainText(node) {
  if (node === null || node === undefined) { return ""; }
  if (typeof node === "string") { return node; }
  if (Array.isArray(node)) { return node.map(plainText).join(""); }
  return plainText(node.i);
}

// Normalise a TOC entry / heading to a comparison key. Handles the real drift
// between the two: **bold**/*italic* markers (already stripped by the parser),
// a trailing "*(Non-Operative)*" parenthetical, "&" vs "and", em/en/figure dash
// variants, and any punctuation / case / whitespace difference.
function tocKey(s) {
  return String(s)
    .replace(/[‐-―−]/g, "-")
    .replace(/\s*\([^()]*\)\s*$/, "")
    .replace(/&/g, " and ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function isHeadingNode(nd) {
  return !!(nd && typeof nd.n === "string" && /^h[1-6]$/.test(nd.n));
}

// Returns { linked: n, unmatched: [text, ...] }.
function linkifyInlineToc(doc) {
  var report = { linked: 0, unmatched: [] };
  if (!doc || !Array.isArray(doc.nodes) || !Array.isArray(doc.toc)) { return report; }

  // Only PART-level entries (sub:false) are candidates — the in-document list
  // names Parts/Preamble/Schedule, never numbered subsections. Restricting the
  // map also stops an entry matching the "Table of Contents" heading itself.
  var byKey = {};
  doc.toc.forEach(function (t) {
    if (t.sub) { return; }
    var k = tocKey(t.text);
    if (k && !Object.prototype.hasOwnProperty.call(byKey, k)) { byKey[k] = t.id; }
  });

  var start = -1;
  for (var x = 0; x < doc.nodes.length; x++) {
    if (isHeadingNode(doc.nodes[x]) && tocKey(plainText(doc.nodes[x])) === "table of contents") {
      start = x; break;
    }
  }
  if (start === -1) { return report; }

  // The first list after that heading, before any following heading.
  var list = null;
  for (var y = start + 1; y < doc.nodes.length; y++) {
    var cand = doc.nodes[y];
    if (!cand || typeof cand !== "object") { continue; }
    if (isHeadingNode(cand)) { break; }
    if ((cand.n === "ul" || cand.n === "ol") && Array.isArray(cand.i)) { list = cand; break; }
  }
  if (!list) { return report; }

  list.c = (list.c ? list.c + " " : "") + "egt-doc__inline-toc";
  list.i.forEach(function (li) {
    if (!li || li.n !== "li") { return; }
    var text = plainText(li);
    var id = byKey[tocKey(text)];
    if (!id) { report.unmatched.push(text); return; }         // leave plain
    li.i = { n: "a", href: "#" + id, c: "egt-doc__inline-toc-link", i: li.i };
    report.linked++;
  });
  return report;
}

exports.render = async function render() {
  var p = fm.resolve("@!/app/content/legal/codex.md");
  var raw = fm.exists(p) ? fm.load(p) : "# The EGT Codex\n\nThe document is being prepared.";
  var doc = mdr.render(raw);
  linkifyInlineToc(doc);
  markConsentList(doc);

  // TOC accordion — Parts / Preamble / Consents / Schedules / Change Log are
  // collapsed primary rows. Click a Part to expand its subsection links under
  // it; only one Part is open at a time (exclusive <details>, wired in
  // egt.legal.js). Flat infinite TOC is gone. The same nav is reused inside
  // the mobile CONTENTS <details> disclosure (CSS swaps which one shows).
  function tocGroups(toc) {
    var groups = [];
    var cur = null;
    (toc || []).forEach(function (t) {
      if (!t.sub) {
        cur = { primary: t, children: [] };
        groups.push(cur);
      } else if (cur) {
        cur.children.push(t);
      }
    });
    return groups;
  }

  function tocNav() {
    var groups = tocGroups(doc.toc);
    return {
      n: "nav", c: "egt-doc__toc-nav", "aria-label": "Codex contents",
      i: groups.map(function (g) {
        var partLink = {
          n: "a", href: "#" + g.primary.id, "data-egt-spy-link": g.primary.id,
          c: "egt-doc__toc-link egt-doc__toc-part", i: g.primary.text
        };
        if (!g.children.length) {
          return { n: "div", c: "egt-doc__toc-group egt-doc__toc-group--leaf", i: [partLink] };
        }
        // Exclusive accordion: native <details>; egt.legal.js closes siblings
        // on toggle and auto-opens the Part that owns the active spy section.
        return {
          n: "details", c: "egt-doc__toc-group", "data-egt-toc-group": g.primary.id,
          i: [
            {
              n: "summary", c: "egt-doc__toc-summary",
              i: [
                partLink,
                { n: "span", c: "egt-doc__toc-chevron", "aria-hidden": "true", i: "" }
              ]
            },
            {
              n: "div", c: "egt-doc__toc-subs",
              i: g.children.map(function (t) {
                return {
                  n: "a", href: "#" + t.id, "data-egt-spy-link": t.id,
                  c: "egt-doc__toc-link is-sub", i: t.text
                };
              })
            }
          ]
        };
      })
    };
  }

  // Mark the slim consents icon-list (first ul under "Start here")
  // so keynote can apply Apple-privacy-style per-row Bootstrap Icons.
  // Class + data-icon + bi span only — no wording change; platform dialog
  // still reads the same markdown bytes.
  function markConsentList(d) {
    if (!d || !Array.isArray(d.nodes)) { return; }
    var ICON_BY_LABEL = {
      "informed consent": "check-circle",
      "data & platform": "speedometer2",
      "health & sensitive information": "heart",
      "privacy": "person-fill",
      "analytics": "grid-3x3-gap",
      "one playbook": "file-earmark-text",
      "children and likeness": "people-fill",
      "arbitration": "bank"
    };
    var BI_BY_ICON = {
      "check-circle": "bi-check-circle",
      "speedometer2": "bi-speedometer2",
      "heart": "bi-heart",
      "person-fill": "bi-person-fill",
      "grid-3x3-gap": "bi-grid-3x3-gap",
      "file-earmark-text": "bi-file-earmark-text",
      "people-fill": "bi-people-fill",
      "bank": "bi-bank"
    };
    var i, nd, txt, list;
    for (i = 0; i < d.nodes.length; i++) {
      nd = d.nodes[i];
      if (!isHeadingNode(nd)) { continue; }
      txt = plainText(nd).replace(/\s+/g, " ").trim().toLowerCase();
      if (txt !== "start here") { continue; }
      for (var j = i + 1; j < d.nodes.length; j++) {
        var cand = d.nodes[j];
        if (isHeadingNode(cand) && /^h[12]$/.test(cand.n)) { break; }
        if (cand && cand.n === "ul" && Array.isArray(cand.i)) { list = cand; break; }
      }
      if (list) {
        list.c = (list.c ? list.c + " " : "") + "egt-doc__consent-list";
        list.i.forEach(function (li) {
          if (!li || li.n !== "li") { return; }
          var label = "";
          var kids = Array.isArray(li.i) ? li.i : (li.i ? [li.i] : []);
          for (var k = 0; k < kids.length; k++) {
            if (kids[k] && kids[k].n === "strong") {
              label = plainText(kids[k]).replace(/\s+/g, " ").trim().toLowerCase();
              break;
            }
          }
          var icon = ICON_BY_LABEL[label];
          if (!icon) { return; }
          li["data-icon"] = icon;
          var bi = BI_BY_ICON[icon] || ("bi-" + icon);
          var ico = { n: "i", c: "bi " + bi + " egt-doc__consent-ico", "aria-hidden": "true", i: "" };
          li.i = [ico].concat(kids);
        });
      }
      return;
    }
  }


  var toc = {
    n: "aside", c: "egt-doc__toc egt-doc__toc--rail",
    i: [
      { n: "p", c: "egt-doc__toc-h", i: "Contents" },
      tocNav()
    ]
  };

  // Mobile / tablet: native <details> disclosure that auto-closes on jump (JS).
  var tocMobile = {
    n: "details", c: "egt-doc__toc-disc", id: "egt-codex-toc-disc",
    i: [
      { n: "summary", c: "egt-doc__toc-disc-sum",
        i: [ { n: "span", i: "Contents" }, { n: "span", c: "egt-doc__toc-disc-x", "aria-hidden": "true", i: "+" } ] },
      tocNav()
    ]
  };

  var masthead = {
    n: "div", c: "egt-doc__head egt-reveal",
    i: [
      { n: "h1", c: "egt-doc__title", i: doc.title }
    ]
  };

  // FIXED full-viewport background — the default pages-background pattern
  // (a real <img>, object-fit:cover, position:fixed at 100vw×100vh behind the
  // content). It is NOT inside .egt-canvas, so it is decoupled from the very
  // tall document and is never stretched; and it leaves NO overflow:hidden
  // ancestor over the sticky TOC rail. Undimmed — contrast comes from the
  // frosted cards only. Sized/pinned by .egt-legal-doc__bg in egt.legal.scss.
  var background = {
    n: "img", c: "egt-legal-doc__bg", src: "/imgs/egt-legal-canvas.jpg",
    alt: "", "aria-hidden": "true", decoding: "async", fetchpriority: "high"
  };

  var section = {
    n: "section", c: "egt-section egt-doc egt-legal-doc",
    i: [
      // reading-progress hairline is now site-wide — S.page() renders it (egt.sections.js).
      { n: "a", href: "#egt-codex-body", c: "egt-doc__skip", i: "Skip to the document" },
      background,
      { n: "div", c: "egt__shell egt-doc__shell",
        i: [
          masthead,
          { n: "div", c: "egt-doc__layout egt-doc--glass",
            i: [
              { n: "div", c: "egt-doc__rail",
                i: [ tocMobile, toc ] },
              { n: "article", c: "egt-doc__body", id: "egt-codex-body",
                "data-egt-spy": "true", i: doc.nodes }
            ] }
        ] }
    ]
  };
  return S.page("/reserve", [ section,
    /* JSON-LD: WebPage + BreadcrumbList (SEO wave 2B-7) */
    S.pageSchema({ path: "/legal/codex", name: "The EGT Codex", crumbs: [ { name: "The EGT Codex" } ] }) ]);
};
